import { useEffect, useRef, useState } from "react";

interface NotificationPopupProps {
    showPopup: boolean;
    popupMessage: string;
    type?: 'info' | 'success' | 'warning' | 'error';
    duration?: number;
    position?: 'top-right' | 'bottom-right' | 'top-left' | 'bottom-left';
}

export default function NotificationPopup({
                                              showPopup,
                                              popupMessage,
                                              type = 'info',
                                              duration = 2000,
                                              position = 'bottom-right'
                                          }: NotificationPopupProps) {
    const [isVisible, setIsVisible] = useState(false);
    const timerRef = useRef<NodeJS.Timeout | null>(null);

    useEffect(() => {
        if (timerRef.current) {
            clearTimeout(timerRef.current);
            timerRef.current = null;
        }
        if (showPopup) {
            setIsVisible(true);
            timerRef.current = setTimeout(() => {
                setIsVisible(false);
                timerRef.current = null;
            }, duration);
        } else {
            setIsVisible(false);
        }
        return () => {
            if (timerRef.current) clearTimeout(timerRef.current);
        };
    }, [showPopup, popupMessage, type, duration]);

    const typeConfig = {
        info: { icon: '🚧', gradient: 'from-gray-100 to-white', border: 'border-gray-200', text: 'text-gray-700' },
        success: { icon: '✅', gradient: 'from-emerald-500 to-emerald-600', border: 'border-emerald-700', text: 'text-white' },
        warning: { icon: '⚠️', gradient: 'from-yellow-500 to-yellow-600', border: 'border-yellow-700', text: 'text-white' },
        error: { icon: '❌', gradient: 'from-red-500 to-red-600', border: 'border-red-700', text: 'text-white' }
    };

    const positionClasses = {
        'top-right': 'top-8 right-8',
        'bottom-right': 'bottom-8 right-8',
        'top-left': 'top-8 left-8',
        'bottom-left': 'bottom-8 left-8'
    };

    if (!isVisible) return null;

    const isInfo = type === 'info';
    return (
        <div className={`fixed z-50 ${positionClasses[position]}`}>
            <div className={`bg-gradient-to-r ${typeConfig[type].gradient} ${typeConfig[type].text} px-5 py-3 rounded-xl flex items-center gap-3 shadow-xl border ${typeConfig[type].border} backdrop-blur-sm min-w-[250px] animate-slideInFromRight`}>
                <span className="text-xl">{typeConfig[type].icon}</span>
                <span className="font-medium">{popupMessage}</span>
            </div>
        </div>
    );
}